import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BLACK,
  Game,
  WHITE,
  colorName,
  defaultKomi,
  formatResult,
  formatVertex,
  toSgf,
  type Color,
  type Vertex,
} from '@baduk/engine';
import { PERSONAS } from '@baduk/bots';
import {
  HINT_LEVELS,
  LESSONS,
  advise,
  checkLesson,
  gradeMoves,
  observe,
  reviewGame,
  startLesson,
  type Advice,
  type GameReview,
  type HoverQuality,
  type Lesson,
  type LessonResult,
} from '@baduk/coach';
import { Board, type Placement } from './components/Board.js';
import { CoachPanel } from './components/CoachPanel.js';
import { HelperPanel } from './components/HelperPanel.js';
import { LessonPanel } from './components/LessonPanel.js';
import { GameLog, describeMove, type LogEntry } from './components/GameLog.js';
import { GamePanel, type GameSetup } from './components/GamePanel.js';
import { ReviewPanel } from './components/ReviewPanel.js';
import {
  DEFAULT_HELPERS,
  atariMarks,
  chainOutlines,
  eyeMarks,
  forbiddenPoints,
  libertyCounts,
  type Helpers,
} from './helpers.js';
import { nextBotTurn } from './turn.js';
import { useBotWorker } from './worker/useBotWorker.js';

type Phase = 'playing' | 'review';

const DEFAULT_SETUP: GameSetup = {
  mode: 'play',
  botId: 'sprout',
  whiteBotId: 'kaze',
  size: 9,
  handicap: 0,
  humanIsBlack: true,
  thinkMs: 1200,
  moveDelayMs: 600,
};

export function App() {
  const [phase, setPhase] = useState<Phase>('playing');

  /**
   * Two copies of the settings, and the reason there are two: the panel beside
   * the board can be edited at any time, but a game already under way has to go
   * on being the game it started as. Editing the live settings would swap a
   * player's opponent mid-fight, or hand both colours to bots halfway through.
   * `draft` is what the panel edits; `active` is what this game is.
   */
  const [draft, setDraft] = useState<GameSetup>(DEFAULT_SETUP);
  const [setup, setSetup] = useState<GameSetup>(DEFAULT_SETUP);

  // The Game is mutable by design (it owns superko history), so it lives in a
  // ref and a version counter drives re-renders.
  const gameRef = useRef<Game>(new Game({ size: DEFAULT_SETUP.size }));
  const [version, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);

  const [log, setLog] = useState<LogEntry[]>([]);
  const [thinking, setThinking] = useState(false);
  const [hintIndex, setHintIndex] = useState(-1);
  const [advice, setAdvice] = useState<Advice | null>(null);
  const [showTerritory, setShowTerritory] = useState(false);
  const [helpers, setHelpers] = useState<Helpers>(DEFAULT_HELPERS);
  const [hovered, setHovered] = useState<Vertex | null>(null);
  /**
   * Stones counted as dead once the game is over. Seeded from the reader and
   * then the player's to correct — the rules settle a finished game by
   * agreement, so the last word has to belong to the people playing it.
   */
  const [dead, setDead] = useState<ReadonlySet<Vertex>>(() => new Set());
  const [helpersOpen, setHelpersOpen] = useState(false);
  const [gameOpen, setGameOpen] = useState(true);
  const [lessonsOpen, setLessonsOpen] = useState(false);
  /** The lesson being attempted, if any. It replaces the game entirely. */
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [lessonResult, setLessonResult] = useState<LessonResult | null>(null);
  const [lessonHint, setLessonHint] = useState(false);
  const [solvedLessons, setSolvedLessons] = useState<ReadonlySet<string>>(() => new Set());
  const [review, setReview] = useState<GameReview | null>(null);
  const [reviewMove, setReviewMove] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paused, setPaused] = useState(false);
  /** Moves the watcher has asked for while paused. */
  const [steps, setSteps] = useState(0);

  const askBot = useBotWorker();
  const game = gameRef.current;
  // A lesson takes the board over: no bot moves, no watching, no review.
  const watching = setup.mode === 'watch' && lesson === null;
  const humanColor: Color = lesson ? lesson.toPlay : setup.humanIsBlack ? BLACK : WHITE;
  const botColor: Color = setup.humanIsBlack ? WHITE : BLACK;
  const persona = PERSONAS[setup.botId] ?? PERSONAS.sprout;
  const whitePersona = PERSONAS[setup.whiteBotId] ?? PERSONAS.kaze;

  const startGame = useCallback(() => {
    setSetup(draft);
    gameRef.current = new Game({
      size: draft.size,
      handicap: draft.handicap,
      rules: { komi: draft.handicap > 0 ? 0.5 : defaultKomi(draft.size) },
    });
    setLog([]);
    setHintIndex(-1);
    setAdvice(null);
    setReview(null);
    setReviewMove(null);
    setError(null);
    setPaused(false);
    setSteps(0);
    setDead(new Set());
    setPhase('playing');
    bump();
  }, [draft, bump]);

  const beginLesson = useCallback(
    (choice: Lesson) => {
      gameRef.current = startLesson(choice);
      setLesson(choice);
      setLessonResult(null);
      setLessonHint(false);
      setLog([]);
      setAdvice(null);
      setHintIndex(-1);
      setReview(null);
      setReviewMove(null);
      setDead(new Set());
      setError(null);
      setPhase('playing');
      bump();
    },
    [bump],
  );

  const leaveLesson = useCallback(() => {
    setLesson(null);
    setLessonResult(null);
    setLessonHint(false);
    startGame();
  }, [startGame]);

  const appendLog = useCallback(
    (moveNumber: number, color: Color, text: string, note?: string) => {
      setLog((entries) => [...entries, { moveNumber, color, text, ...(note ? { note } : {}) }]);
    },
    [],
  );

  const finishIfOver = useCallback(() => {
    const current = gameRef.current;
    if (!current.isOver) return false;
    // Watching has no "your moves" to single out, so the review covers both sides.
    setDead(new Set(current.deadStones()));
    setReview(reviewGame(current, watching ? {} : { forColor: humanColor }));
    setPhase('review');
    return true;
  }, [humanColor, watching]);

  const playHuman = useCallback(
    (vertex: Vertex) => {
      const current = gameRef.current;
      if (current.isOver || current.toPlay !== humanColor) return;
      try {
        const record = current.play({ type: 'play', vertex });
        appendLog(current.moveCount, humanColor, describeMove(current.size, record.move, record.captured.length));
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : String(caught));
        return;
      }
      setAdvice(null);
      setHintIndex(-1);
      setError(null);
      bump();

      // In a lesson the move is the answer, and the verdict is immediate: the
      // reader can say whether the group lives without anyone replying.
      if (lesson) {
        const verdict = checkLesson(lesson, current);
        setLessonResult(verdict);
        if (verdict.status === 'solved') {
          setSolvedLessons((done) => new Set(done).add(lesson.id));
        }
        return;
      }
      finishIfOver();
    },
    [appendLog, bump, finishIfOver, humanColor, lesson],
  );

  const humanPass = useCallback(() => {
    const current = gameRef.current;
    if (current.isOver || current.toPlay !== humanColor) return;
    current.pass();
    appendLog(current.moveCount, humanColor, 'passes');
    bump();
    finishIfOver();
  }, [appendLog, bump, finishIfOver, humanColor]);

  const humanResign = useCallback(() => {
    const current = gameRef.current;
    if (current.isOver || current.toPlay !== humanColor) return;
    current.resign();
    appendLog(current.moveCount, humanColor, 'resigns');
    bump();
    finishIfOver();
  }, [appendLog, bump, finishIfOver, humanColor]);

  /** Undo takes back the bot's reply as well, so the learner gets their turn back. */
  const undo = useCallback(() => {
    const current = gameRef.current;
    let removed = 0;
    while (removed < 2 && current.moveCount > 0) {
      current.undo();
      removed++;
      if (current.toPlay === humanColor) break;
    }
    setLog((entries) => entries.slice(0, Math.max(0, entries.length - removed)));
    setAdvice(null);
    setHintIndex(-1);
    setReview(null);
    setDead(new Set());
    setPhase('playing');
    bump();
  }, [bump, humanColor]);

  // --- Whoever's turn it is, if a bot owns it -------------------------------
  /**
   * The in-flight guard is a ref, not the `thinking` state, and deliberately so.
   * As state it would have to be a dependency of the effect below, and setting it
   * would re-run the effect and tear down the request that had just been made —
   * so a reply would arrive already discarded. Playing hides that, because the
   * next human click starts the loop again; watching does not, because there is
   * no click to come. `thinking` now only drives what the screen says.
   */
  const inFlight = useRef(false);

  useEffect(() => {
    if (phase !== 'playing' || lesson) return;
    const current = gameRef.current;
    const turn = nextBotTurn({
      setup,
      toPlay: current.toPlay,
      isOver: current.isOver,
      thinking: inFlight.current,
      paused,
      steps,
    });
    if (turn === null) return;

    const movesWhenAsked = current.moveCount;
    inFlight.current = true;
    setThinking(true);
    let asked = false;

    const timer = setTimeout(() => {
      asked = true;
      askBot(current, turn.botId, setup.thinkMs)
        .then((response) => {
          // A reply is only good for the position it was asked about — a restart
          // or an undo in the meantime makes it stale.
          if (gameRef.current !== current || current.moveCount !== movesWhenAsked) return;
          if (!response.ok) {
            setError(response.error);
            return;
          }
          const record = current.play(response.move);
          appendLog(
            current.moveCount,
            record.color,
            describeMove(current.size, record.move, record.captured.length),
            response.rationale,
          );
          setSteps((n) => Math.max(0, n - 1));
          bump();
          finishIfOver();
        })
        .catch((caught: unknown) => {
          setError(caught instanceof Error ? caught.message : String(caught));
        })
        .finally(() => {
          inFlight.current = false;
          setThinking(false);
        });
    }, turn.delayMs);

    return () => {
      clearTimeout(timer);
      // Releasing the guard is only safe while nothing has actually been asked;
      // once it has, the reply resolves it instead.
      if (!asked) {
        inFlight.current = false;
        setThinking(false);
      }
    };
    // `version` is the trigger: every completed move re-evaluates whose turn it is.
  }, [phase, version, setup, paused, steps, askBot, appendLog, bump, finishIfOver]);

  // --- Coach ----------------------------------------------------------------
  // `version` is the real dependency here: the Game is mutable, so a bumped
  // version is what signals that the position changed.
  const observations = useMemo(() => {
    if (phase !== 'playing' || game.isOver) return [];
    // Watching, the coach comments on whoever is about to move — the point is to
    // see what each bot is walking into, not to root for one of them.
    if (watching) return observe(game, game.toPlay);
    if (game.toPlay !== humanColor) return [];
    return observe(game, humanColor);
  }, [phase, version, humanColor, game, watching]);

  // Scrubbing the review shows an earlier position, and the overlays have to be
  // about the board in front of the reader rather than the one at the end.
  const displayed = reviewMove === null ? game.position : game.positionAt(reviewMove);
  const lastRecord = game.history.at(-1);
  const lastMove =
    reviewMove === null && lastRecord && lastRecord.move.type === 'play'
      ? lastRecord.move.vertex
      : null;

  /**
   * Board overlays. Each is off unless asked for, and each is recomputed only
   * when the position changes — `version` is the real dependency, since the Game
   * is mutable and its identity never changes.
   */
  const ataris = useMemo(
    () => (helpers.atari ? atariMarks(displayed) : []),
    [helpers.atari, displayed],
  );
  const eyes = useMemo(() => (helpers.eyes ? eyeMarks(displayed) : []), [helpers.eyes, displayed]);
  const counts = useMemo(
    () => (helpers.counts ? libertyCounts(displayed, lastMove) : []),
    [helpers.counts, displayed, lastMove],
  );
  const outlines = useMemo(
    () => (helpers.outlines ? chainOutlines(displayed) : []),
    [helpers.outlines, displayed],
  );
  const forbidden = useMemo(
    // A ko ban belongs to the turn it is on. Rewound to an earlier move, there is
    // no turn to take, so there is nothing honest to draw.
    () => (helpers.ko && !game.isOver && reviewMove === null ? forbiddenPoints(game, game.toPlay) : []),
    [helpers.ko, version, game, reviewMove],
  );

  /**
   * Live feedback, graded once for the whole board rather than per hover.
   *
   * Only for the player on move, and only while they can actually play: grading
   * points during the opponent's turn would be answering a question nobody can
   * act on. `version` is the dependency that matters, since the Game is mutable.
   */
  const feedback = useMemo(() => {
    if (!helpers.feedback || watching || reviewMove !== null) return null;
    if (game.isOver || game.toPlay !== humanColor) return null;
    return gradeMoves(game, humanColor);
  }, [helpers.feedback, watching, reviewMove, version, game, humanColor]);

  const quality = useMemo(() => {
    if (!feedback) return undefined;
    const map = new Map<Vertex, HoverQuality>();
    for (const [vertex, note] of feedback) map.set(vertex, note.quality);
    return map;
  }, [feedback]);

  const hoveredFeedback = hovered === null ? null : (feedback?.get(hovered) ?? null);

  /**
   * The coach's shortlist, drawn on the board.
   *
   * Only once the hint has reached the level that names a move. Showing ranked
   * placements at the nudge or area level would collapse the three tiers into
   * one, and the pause between those clicks is where the learning happens.
   */
  const candidates: Placement[] = useMemo(() => {
    if (advice?.vertex === undefined) return [];
    return advice.alternatives.slice(0, 5).map((candidate, index) => ({
      vertex: candidate.vertex,
      rank: index + 1,
    }));
  }, [advice]);

  /** The board settles dead stones once the game is over and history is not being scrubbed. */
  const marking = phase === 'review' && game.isOver && reviewMove === null;

  const toggleDead = useCallback((vertex: Vertex) => {
    const group = gameRef.current.position.group(vertex);
    if (!group) return;
    setDead((current) => {
      const next = new Set(current);
      const removing = group.stones.every((stone) => next.has(stone));
      for (const stone of group.stones) {
        if (removing) next.delete(stone);
        else next.add(stone);
      }
      return next;
    });
  }, []);

  const requestHint = useCallback(() => {
    const next = Math.min(hintIndex + 1, HINT_LEVELS.length - 1);
    setHintIndex(next);
    setAdvice(advise(gameRef.current, humanColor, { level: HINT_LEVELS[next] }));
  }, [hintIndex, humanColor]);

  const clearHint = useCallback(() => {
    setHintIndex(-1);
    setAdvice(null);
  }, []);

  const downloadSgf = useCallback(() => {
    const sgf = toSgf(gameRef.current, {
      playerBlack: watching ? persona.name : setup.humanIsBlack ? 'You' : persona.name,
      playerWhite: watching ? whitePersona.name : setup.humanIsBlack ? persona.name : 'You',
      event: watching ? 'Baduk bot arena' : 'Baduk playspace',
    });
    const blob = new Blob([sgf], { type: 'application/x-go-sgf' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `baduk-${new Date().toISOString().slice(0, 10)}.sgf`;
    anchor.click();
    URL.revokeObjectURL(url);
  }, [persona.name, whitePersona.name, setup.humanIsBlack, watching]);

  // Once the game is over the score is the real one, and it counts the dead.
  const score = game.score(phase === 'review' ? dead : undefined);
  const waiting = thinking || game.toPlay !== humanColor || game.isOver;

  /**
   * Black first, then White — the seats, whoever is sitting in them. The colour
   * is the name and the occupant is the subtitle, matching how the coach talks
   * about the board: it says Black and White, so the cards should agree.
   */
  const seats: { color: Color; who: string; avatar: string }[] = watching
    ? [
        { color: BLACK, who: persona.name, avatar: persona.avatar },
        { color: WHITE, who: whitePersona.name, avatar: whitePersona.avatar },
      ]
    : [
        { color: humanColor, who: 'You', avatar: '🧑' },
        { color: botColor, who: persona.name, avatar: persona.avatar },
      ];
  seats.sort((a, b) => a.color - b.color);

  return (
    <main className="app">
      <aside className="rail rail-left">
        <h1 className="app-title">Baduk</h1>

        <GamePanel
          draft={draft}
          onChange={setDraft}
          active={setup}
          onStart={startGame}
          open={gameOpen}
          onOpenChange={setGameOpen}
          moveCount={game.moveCount}
        />

        <LessonPanel
          lessons={LESSONS}
          active={lesson}
          result={lessonResult}
          solved={solvedLessons}
          hintShown={lessonHint}
          onStart={beginLesson}
          onRetry={() => lesson && beginLesson(lesson)}
          onHint={() => setLessonHint(true)}
          onLeave={leaveLesson}
          open={lessonsOpen}
          onOpenChange={setLessonsOpen}
        />

        {phase === 'review' && review ? (
          <ReviewPanel
            game={game}
            review={review}
            onJumpTo={setReviewMove}
            onRematch={startGame}
            onNewOpponent={() => setGameOpen(true)}
            onDownloadSgf={downloadSgf}
          />
        ) : lesson ? null : (
          <CoachPanel
            size={game.size}
            observations={observations}
            advice={advice}
            hintIndex={Math.max(0, hintIndex)}
            onHint={requestHint}
            onClearHint={clearHint}
            disabled={waiting}
            showHints={!watching}
            {...(watching
              ? { subject: game.toPlay === BLACK ? persona.name : whitePersona.name }
              : {})}
          />
        )}
      </aside>

      <div className="board-column">
        <div className="players">
          {seats.map((seat) => (
            <div
              key={seat.color}
              className={`player${game.toPlay === seat.color && !game.isOver ? ' active' : ''}`}
            >
              <span className="player-avatar" aria-hidden="true">
                {seat.avatar}
              </span>
              <div>
                <strong>{colorName(seat.color)}</strong>
                <span className="muted small">
                  {seat.who} · {game.position.captures[seat.color - 1]} captured
                  {thinking && game.toPlay === seat.color ? ' · thinking…' : ''}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="board-wrap">
          <Board
            position={displayed}
            lastMove={lastMove}
            toPlay={game.toPlay}
            onPlay={playHuman}
            legal={(v) => game.isLegal({ type: 'play', vertex: v }, watching ? game.toPlay : humanColor)}
            disabled={watching || waiting || reviewMove !== null}
            coachHighlight={advice?.region ?? observations[0]?.highlight ?? []}
            coachMove={advice?.vertex ?? null}
            candidates={candidates}
            showTerritory={showTerritory}
            showCoordinates={helpers.coordinates}
            showLiberties={helpers.liberties}
            ataris={ataris}
            eyes={eyes}
            libertyCounts={counts}
            outlines={outlines}
            forbidden={forbidden}
            {...(quality ? { quality } : {})}
            {...(marking ? { dead, onMarkDead: toggleDead } : {})}
            onHoverChange={setHovered}
          />
        </div>

        {marking && (
          <p className="marking-note muted small">
            {dead.size > 0
              ? `${dead.size} stone${dead.size === 1 ? '' : 's'} counted as dead. `
              : 'Nothing is marked dead. '}
            Click any group to change its verdict — the score follows.
          </p>
        )}

        {/*
          The feedback strip keeps a fixed height whether or not a point is
          hovered, so the board and the controls under it do not jump about as
          the cursor crosses the grid.
        */}
        {helpers.feedback && !watching && (
          <div
            className={`live-feedback${hoveredFeedback ? ` grade-${hoveredFeedback.quality}` : ''}`}
            aria-live="polite"
          >
            {hoveredFeedback ? (
              <>
                <span className="grade-badge">{hoveredFeedback.label}</span>
                <span className="live-feedback-text">
                  <strong>{formatVertex(game.size, hoveredFeedback.vertex)}</strong>{' '}
                  {hoveredFeedback.note}
                  {hoveredFeedback.reason && (
                    <span className="muted small"> {hoveredFeedback.reason}</span>
                  )}
                </span>
              </>
            ) : (
              <span className="muted small">
                {game.toPlay === humanColor && !game.isOver
                  ? 'Hover a point to see what the coach makes of it — and try to guess before you look.'
                  : 'Waiting for your turn.'}
              </span>
            )}
          </div>
        )}

        <div className="controls">
          {watching ? (
            <>
              <button
                type="button"
                className="primary"
                onClick={() => setPaused((on) => !on)}
                disabled={game.isOver}
              >
                {paused ? 'Resume' : 'Pause'}
              </button>
              <button
                type="button"
                onClick={() => setSteps((n) => n + 1)}
                disabled={game.isOver || !paused || thinking}
              >
                One move
              </button>
            </>
          ) : (
            <>
              <button type="button" onClick={humanPass} disabled={waiting}>
                Pass
              </button>
              {/* Undoing mid-think would let a stale bot reply land on a board that
                  no longer matches the position it was asked about. */}
              <button type="button" onClick={undo} disabled={game.moveCount === 0 || thinking}>
                Undo
              </button>
            </>
          )}
          {watching ? (
            <button type="button" onClick={() => setGameOpen(true)}>
              New matchup
            </button>
          ) : (
            <button type="button" onClick={humanResign} disabled={waiting}>
              Resign
            </button>
          )}
          {reviewMove !== null && (
            <button type="button" onClick={() => setReviewMove(null)}>
              Back to the end
            </button>
          )}

          {/*
            The state of the game rides along in the toolbar rather than on a
            line of its own below it. It is the number a player checks most, and
            it used to be the smallest grey text on the page, furthest down.
          */}
          <p className="status muted small">
            {game.isOver
              ? `${formatResult(score)} · ${score.black} – ${score.white}`
              : `${colorName(game.toPlay)} to play · ${score.black} – ${score.white}`}
            {reviewMove !== null ? ` · move ${reviewMove}` : ''}
          </p>
        </div>

        {error && <p className="error">{error}</p>}
      </div>

      <aside className="rail rail-right">
        <HelperPanel
          helpers={helpers}
          onChange={setHelpers}
          showTerritory={showTerritory}
          onToggleTerritory={setShowTerritory}
          open={helpersOpen}
          onOpenChange={setHelpersOpen}
        />

        <section className="log-section">
          <h2>Moves</h2>
          <GameLog entries={log} />
        </section>
      </aside>
    </main>
  );
}
